import { translate } from 'i18n/i18n'
import { useScreenSize } from 'utils/useScreenSize'
import type { WeatherForecastAlertOnly } from '../../../_types'
import { unitSpeedDisplay, unitTempDisplay } from '../../../settings/alertSettings'
import { Separator } from '../RulesRow/RulesRow'

const getRuleLabels = ({
  rule,
  windUnit,
  tempUnit,
}: {
  rule: WeatherForecastAlertOnly['rules']
  windUnit: string
  tempUnit: string
}) => {
  if ('hasRain' in rule) {
    return [translate.phrases.banyanApp('Rain is forecast in the next 7 days')]
  }

  if ('windGreaterThanOrEqualToUserUnits' in rule) {
    return [
      translate.phrases.banyanApp('Wind Speed >= {{ windSpeed }} {{ unit }} in the next 7 days', {
        windSpeed: rule.windGreaterThanOrEqualToUserUnits,
        unit: windUnit,
      }),
    ]
  }

  let temperatureLabels: string[] = []

  if ('temperatureGreaterThanOrEqualToUserUnits' in rule) {
    temperatureLabels = (rule.measurementHeights || []).map((m) => {
      let label = ''

      if (m === 'aboveCanopyTemperature') label = 'Above Canopy Temperature'

      if (m === 'inCanopyTemperature') label = 'In Canopy Temperature'

      return translate.phrases.banyanApp('{{ label }} >= {{ temperature }} {{ unit }} in the next 7 days', {
        label,
        temperature: rule.temperatureGreaterThanOrEqualToUserUnits,
        unit: tempUnit,
      })
    })
  }

  if ('temperatureLessThanOrEqualToUserUnits' in rule) {
    temperatureLabels = (rule.measurementHeights || []).map((m) => {
      let label = ''

      if (m === 'aboveCanopyTemperature') label = 'Above Canopy Temperature'

      if (m === 'inCanopyTemperature') label = 'In Canopy Temperature'

      return translate.phrases.banyanApp('{{ label }} <= {{ temperature }} {{ unit }} in the next 7 days', {
        label,
        temperature: rule.temperatureLessThanOrEqualToUserUnits,
        unit: tempUnit,
      })
    })
  }

  if ('includeAllDevices' in rule) {
    temperatureLabels.push(
      translate.phrases.banyanApp('Include all devices = {{ value }}', { value: rule.includeAllDevices }),
    )
  }

  return temperatureLabels
}

export const WeatherForecastRulesRow = ({ rules }: Pick<WeatherForecastAlertOnly, 'rules'>) => {
  const windUnit = unitSpeedDisplay()
  const tempUnit = unitTempDisplay()
  const { isWideScreen } = useScreenSize()

  const labels = getRuleLabels({
    rule: rules,
    windUnit,
    tempUnit,
  })

  const rulesRow = labels.map((r, index) => {
    return (
      <div
        key={index}
        css={{
          margin: 0,
          textAlign: 'left',
          display: 'flex',
          flexDirection: isWideScreen ? 'row' : 'column',
        }}
      >
        {index > 0 && isWideScreen && <Separator />}

        <div css={{ display: 'flex', flexDirection: isWideScreen ? 'row' : 'column' }}>{r}</div>
      </div>
    )
  })

  return (
    <div css={{ display: 'flex', flexDirection: isWideScreen ? 'row' : 'column', flexWrap: 'wrap' }}>
      {rulesRow}
    </div>
  )
}
